import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { useGhostClickGuard } from './ghostClick';
import styles from './Sheet.module.css';

interface SheetProps {
  readonly label: string;
  readonly onClose: () => void;
  /** Esc, when it should differ from a press on the backdrop. Defaults to `onClose`. */
  readonly onEscape?: () => void;
  readonly children: ReactNode;
}

/**
 * A bottom sheet on a native modal `<dialog>`: focus trap, Esc and the backdrop are built in. It
 * opens in a layout effect, so its content can move focus inside it from its own (passive) effects.
 */
export function Sheet({ label, onClose, onEscape, children }: SheetProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const ghostGuard = useGhostClickGuard();

  useLayoutEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
    return () => {
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    };
  }, []);

  return (
    <dialog
      ref={ref}
      className={styles.sheet}
      aria-label={label}
      data-testid="sheet"
      onCancel={(event) => {
        event.preventDefault();
        (onEscape ?? onClose)();
      }}
      // Pointer down, not click: on touch the click that follows the tap which opened the sheet
      // lands on the dialog and would close it again at once.
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      // That same trailing click can instead land on a sheet control (a project row of the
      // taller picker): the guard swallows any pointer click without a pointerdown inside.
      onPointerDownCapture={ghostGuard.onPointerDownCapture}
      onClickCapture={ghostGuard.onClickCapture}
    >
      <div className={styles.content}>{children}</div>
    </dialog>
  );
}
